import { Phone } from 'lucide-react';
import { CallsConferenceStatus } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/CallsTab/CallsConferenceStatus';
import { CallsControlsStatus } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/CallsTab/CallsControlsStatus';
import { CallsList } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/CallsTab/CallsList';
import { CallsSummary } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/CallsTab/CallsSummary';
import { CallsTransferStatus } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/CallsTab/CallsTransferStatus';
import { formatTimestamp } from 'Modules/softphone/components/SoftphoneDevTools/utils';
import { useSoftphoneStore } from 'Modules/softphone/store';
import React from 'react';
import { useShallow } from 'zustand/react/shallow';

/**
 * CallsTab - What the softphone is doing right now
 */
export const CallsTab: React.FC = () => {
  const {
    activeCalls,
    incomingCalls,
    currentCallId,
    activeConference,
    isMerging,
    lastRegistrationTime,
  } = useSoftphoneStore(
    useShallow((state) => ({
      activeCalls: state.activeCalls,
      incomingCalls: state.incomingCalls,
      currentCallId: state.currentCallId,
      activeConference: state.activeConference,
      isMerging: state.isMerging,
      lastRegistrationTime: state.lastRegistrationTime,
    }))
  );

  const allActiveCalls = Array.from(activeCalls.values());
  const incomingCallsArray = Array.from(incomingCalls.values());

  // Separate active calls from held calls
  const activeCallsArray = allActiveCalls.filter((call) => !call.isOnHold);
  const heldCallsArray = allActiveCalls.filter((call) => call.isOnHold);

  const hasCalls = allActiveCalls.length > 0 || incomingCallsArray.length > 0;

  return (
    <div className="flex flex-col gap-3">
      <CallsSummary
        active={activeCallsArray.length}
        onHold={heldCallsArray.length}
        incoming={incomingCallsArray.length}
      />

      <CallsTransferStatus />

      <CallsConferenceStatus
        isMerging={isMerging}
        activeConference={activeConference}
        activeCalls={activeCalls}
        currentCallId={currentCallId}
      />

      {hasCalls ? (
        <>
          {/* Each list renders only when it has something in it — the counters
              above already answer "how many", so an empty box would repeat it. */}
          <CallsList
            title="Active"
            calls={activeCallsArray}
            currentCallId={currentCallId}
          />
          <CallsList
            title="On hold"
            calls={heldCallsArray}
            currentCallId={currentCallId}
          />
          <CallsList
            title="Incoming"
            calls={incomingCallsArray}
            currentCallId={currentCallId}
          />
        </>
      ) : (
        <div className="flex items-center gap-3 rounded-[10px] border border-zinc-800 bg-zinc-900 px-4 py-[18px]">
          <span className="flex h-[38px] w-[38px] flex-shrink-0 items-center justify-center rounded-[11px] border border-zinc-800 bg-zinc-900">
            <Phone className="h-[18px] w-[18px] text-zinc-400" strokeWidth={1.7} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold text-zinc-200">
              Nothing on the wire
            </p>
            <p className="mt-0.5 text-[11.5px] leading-[1.5] text-zinc-300">
              {lastRegistrationTime ? (
                <>
                  Registered since{' '}
                  <span className="font-sp-mono">
                    {formatTimestamp(lastRegistrationTime, { fallback: '—' })}
                  </span>{' '}
                  — active, held and incoming calls appear here the moment they
                  signal.
                </>
              ) : (
                'Not registered, so no call can arrive. The SIP tab has the reason.'
              )}
            </p>
          </div>
        </div>
      )}

      <CallsControlsStatus />
    </div>
  );
};
